/**
 * TireVisual — Car-shaped tire visualization with heatmap zones.
 * Ported from SimSense (osc-telemetry) SessionOverview.tsx TireVisualPanel.
 * Adapted for StintSense color scheme and font stack.
 */

import type { TireData, TireCorner } from "../types";

interface Props {
  tireData: TireData | null;
  unit?: string; // "standard" (PSI, F) or "metric" (kPa, C)
}

// Temperature → heatmap color (Fahrenheit input)
function tireTempColor(tempF: number | null | undefined): string {
  if (!tempF) return "rgba(255,255,255,0.06)";
  if (tempF < 180) return "#15803d";  // cool dark green
  if (tempF < 210) return "#22c55e";  // optimal green
  if (tempF < 230) return "#4ade80";  // warm light green
  if (tempF < 250) return "#86efac";  // upper green
  if (tempF < 265) return "#f97316";  // orange
  if (tempF < 280) return "#ea580c";  // dark orange
  return "#dc2626";                    // red (too hot)
}

function formatTemp(val: number | null | undefined, unit: string): string {
  if (val == null) return "—";
  return `${val.toFixed(0)}°${unit === "metric" ? "C" : "F"}`;
}

function formatPressure(val: number | null | undefined, unit: string): string {
  if (val == null) return "—";
  return `${val.toFixed(1)} ${unit === "metric" ? "kPa" : "PSI"}`;
}

function formatWear(val: number | null | undefined): string {
  if (val == null) return "—";
  return `${val.toFixed(0)}%`;
}

export default function TireVisual({ tireData, unit = "standard" }: Props) {
  if (!tireData || tireData.confidence === "none") {
    return (
      <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", fontSize: "0.94rem" }}>
        No tire data available
      </div>
    );
  }

  const confidence = tireData.confidence;
  const provenance = (tireData as unknown as Record<string, unknown>).provenance as string | undefined;

  return (
    <div style={{
      background: "rgba(0,0,0,0.15)",
      padding: "1rem 0.75rem",
      borderRadius: "var(--r-lg)",
      border: "1px solid var(--rule)",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <span style={{
          fontFamily: "var(--font-body)", fontSize: "0.82rem", fontWeight: 500,
          color: "var(--text-secondary)",
        }}>
          Tires (end of run)
        </span>
        {confidence !== "full" && (
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.82rem", fontWeight: 500, padding: "2px 6px", borderRadius: 3,
            background: confidence === "partial" ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.06)",
            color: confidence === "partial" ? "#f59e0b" : "var(--text-secondary)",
          }}>
            {confidence === "partial" ? "Estimated" : "No data"}
          </span>
        )}
        {provenance === "sidecar" && (
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.82rem", fontWeight: 500, padding: "2px 6px", borderRadius: 3,
            background: "var(--live-soft)", color: "var(--live)",
          }}>
            Garage data
          </span>
        )}
      </div>

      {/* Front row */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "0.75rem" }}>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.LF} label="Left" isRight={false} unit={unit} />
        </div>
        <div style={{ width: 40, textAlign: "center", paddingTop: 14 }}>
          <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "var(--text-muted)" }}>Front</span>
        </div>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.RF} label="Right" isRight={true} unit={unit} />
        </div>
      </div>

      {/* Rear row */}
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.LR} label="" isRight={false} unit={unit} />
        </div>
        <div style={{ width: 40, textAlign: "center", paddingTop: 14 }}>
          <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "var(--text-muted)" }}>Rear</span>
        </div>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.RR} label="" isRight={true} unit={unit} />
        </div>
      </div>
    </div>
  );
}

function TireShape({
  data,
  label,
  isRight,
  unit,
}: {
  data: TireCorner | null;
  label: string;
  isRight: boolean;
  unit: string;
}) {
  if (!data) {
    return (
      <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
        {label && <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--text-muted)", marginBottom: 2 }}>{label}</div>}
        <div style={{ fontSize: "0.84rem", color: "var(--text-muted)", padding: "1rem 0" }}>—</div>
      </div>
    );
  }

  const displayTemps = data.surfaceTemps ?? data.temps;
  const tempO = displayTemps?.R; // Outside = R for left tires, L for right tires
  const tempM = displayTemps?.M;
  const tempI = displayTemps?.L; // Inside = L for left tires, R for right tires
  const pressure = data.hotPressure;
  const wearO = data.wear?.R;
  const wearM = data.wear?.M;
  const wearI = data.wear?.L;

  // Left tires: O|M|I left-to-right. Right tires: I|M|O left-to-right.
  const zones = isRight
    ? [{ t: tempI, w: wearI, l: "I" }, { t: tempM, w: wearM, l: "M" }, { t: tempO, w: wearO, l: "O" }]
    : [{ t: tempO, w: wearO, l: "O" }, { t: tempM, w: wearM, l: "M" }, { t: tempI, w: wearI, l: "I" }];

  // For heatmap coloring — if temps are in Celsius, convert to F for the color function
  const toF = (val: number | null | undefined): number | null | undefined => {
    if (val == null) return val;
    if (unit === "metric") return val * 9 / 5 + 32;
    return val; // Already Fahrenheit
  };

  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Corner label */}
      {label && (
        <div style={{ fontFamily: "var(--font-body)", fontSize: "0.88rem", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 2 }}>
          {label}
        </div>
      )}

      {/* O/M/I zone labels */}
      <div style={{ display: "flex", width: "100%", justifyContent: "space-around", marginBottom: 2 }}>
        {zones.map((z, i) => (
          <span key={i} style={{ fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-muted)", textAlign: "center", flex: 1 }}>
            {z.l}
          </span>
        ))}
      </div>

      {/* Tire shape — 3 colored zone strips */}
      <div style={{
        display: "flex", width: "100%", height: 52,
        borderRadius: "8px 8px 4px 4px", overflow: "hidden",
        border: "2px solid rgba(255,255,255,0.1)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.3), inset 0 2px 4px rgba(0,0,0,0.15)",
      }}>
        {zones.map((z, i) => (
          <div key={i} style={{
            flex: 1,
            background: tireTempColor(toF(z.t)),
            borderRight: i < 2 ? "1px solid rgba(0,0,0,0.25)" : "none",
          }} />
        ))}
      </div>

      {/* Temp values */}
      <div style={{ display: "flex", width: "100%", marginTop: 3 }}>
        {zones.map((z, i) => (
          <span key={i} style={{
            flex: 1, textAlign: "center", fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
            fontSize: "0.88rem", fontWeight: 700, color: "var(--text-strong)",
          }}>
            {formatTemp(z.t, unit)}
          </span>
        ))}
      </div>

      {/* Wear values */}
      <div style={{ display: "flex", width: "100%", marginTop: 1 }}>
        {zones.map((z, i) => (
          <span key={i} style={{
            flex: 1, textAlign: "center", fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
            fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-muted)",
          }}>
            {formatWear(z.w)}
          </span>
        ))}
      </div>

      {/* Pressure */}
      <div style={{
        fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums", fontSize: "0.82rem", fontWeight: 600,
        color: "var(--text-secondary)", marginTop: 3,
      }}>
        {formatPressure(pressure, unit)}
      </div>
    </div>
  );
}
